import React, { useEffect, useState, useCallback } from "react";
import {
  SearchOutlined,
  EyeOutlined,
  PlusOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { Table, Button, Input, Pagination, Modal } from "antd";
import {
  GET_ALL_ACTIVE_STUDENTS,
  GET_STUDENT_MEDICAL_DETAILS,
} from "../../Utlis/apiPath";
import { getApi } from "../../Utlis/apiService";
import debounce from "lodash.debounce";
import { useNavigate } from "react-router-dom";
import Buttons from "../../Components/Buttons/Buttons";
import Loader from "../../Components/Loader/Loader";
import "./Students.scss";

const ActiveStudents = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [medicalDetails, setMedicalDetails] = useState(null);
  const [isAddStudentModalVisible, setIsAddStudentModalVisible] = useState(false);
  const [isAscending, setIsAscending] = useState(null);
  const Navigate = useNavigate();

  // Debounce function
  const debounceSearch = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setPageIndex(1);
    }, 800),
    []
  );

  const handleViewClasses = async (studentId) => {
    Navigate(`/view-booked-classes/${studentId}`);
  };

  useEffect(() => {
    fetchAllActiveStudents();
  }, [debouncedSearch, pageIndex, pageSize]);

  const fetchAllActiveStudents = async () => {
    setIsLoading(true);
    try {
      const url = `${GET_ALL_ACTIVE_STUDENTS}`;
      const params = {
        search: debouncedSearch,
        pageIndex,
        pageSize,
      };

      const { status, data } = await getApi(url, { params });
      if (status === 200) {
        const { students, totalRecords } = data;
        // Safety net: the API sometimes ignores pageIndex/pageSize and returns
        // every matching record, so slice out just the current page ourselves.
        const start = (pageIndex - 1) * pageSize;
        const pageStudents = Array.isArray(students)
          ? students.slice(start, start + pageSize)
          : [];
        const updatedData = pageStudents.map((student, index) => ({
          ...student,
          slNo: start + index + 1,
          key: student.studentId || student.id || index,
        }));
        setTableData(updatedData);
        setTotalRecords(totalRecords);
      } else {
        setTableData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching active students:", error);
      setTableData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    debounceSearch(e.target.value);
  };

  const handlePageChange = (page, size) => {
    setPageIndex(page);
    setPageSize(size);
  };

  const showMedicalDetailsModal = async (studentId) => {
    setIsLoading(true);
    try {
      const url = `${GET_STUDENT_MEDICAL_DETAILS}?userId=${studentId}`;
      const { status, data } = await getApi(url);
      if (status === 200 && data && data.medicalFitness) {
        setMedicalDetails(data);
        setIsModalVisible(true);
      } else {
        setMedicalDetails(null);
      }
    } catch (error) {
      console.error("Error fetching medical details:", error);
      setMedicalDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelMedicalDetailsModal = () => {
    setIsModalVisible(false);
    setMedicalDetails(null);
  };

  const studentColumns = [
    // { title: "Sl. No", dataIndex: "slNo", key: "slNo" },
    { 
      title: "Roll No", 
      dataIndex: "rollNo", 
      key: "rollNo",
      render: (text) => (text ? text : "N/A"),
    },
    { title: "Student Name", dataIndex: "studentName", key: "studentName" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Contact Number",
      dataIndex: "contactNumber",
      key: "contactNo",
      render: (contactNumber) => {
        if (!contactNumber) return "-";
        
        // Remove + if exists
        let cleanNumber = contactNumber.replace(/^\+/, "");
        
        // For India: +91 followed by 10 digits (total 12 digits)
        if (cleanNumber.startsWith("91") && cleanNumber.length === 12) {
          const countryCode = cleanNumber.substring(0, 2);
          const phoneDigits = cleanNumber.substring(2);
          return `+${countryCode}-${phoneDigits}`;
        }
        
        // For other country codes with more than 10 digits
        const match = cleanNumber.match(/^(\d{1,3})(\d{10,})$/);
        if (match) {
          return `+${match[1]}-${match[2]}`;
        }
        
        // If no country code, just show the phone number as is
        return contactNumber;
      },
    },
    {
      title: "Country",
      dataIndex: "countryName",
      key: "country",
      render: (text) => (text && text.trim() ? text : "N/A"),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (text) => (text && text.trim() ? text : "N/A"),
    },
    {
      title: "Classes & Instructors",
      key: "classInfo",
      render: (_, record) => (
        <Button
          onClick={() => handleViewClasses(record.studentId)}
          size="small"
          style={{ marginLeft: "20px" }}
        >
          view details
        </Button>
      ),
    },
    {
      title: "Personal Info",
      key: "personalInfo",
      render: (_, record) => (
        <div
          style={{
            display: "inline-block",
            padding: "4px 8px",
            border: "2px solid #1890ff",
            borderRadius: "4px",
            cursor: "pointer",
            marginLeft: "20px",
          }}
          onClick={() => showMedicalDetailsModal(record.studentId)}
        >
          <EyeOutlined
            style={{
              fontSize: "16px",
              color: "#1890ff",
            }}
          />
        </div>
      ),
    },
  ];

  let studentAddress = "";

  if (medicalDetails) {
    const userAddress = medicalDetails?.addresses.find(
      (address) => address.isDefault
    );
    for (let key in userAddress) {
      if (key !== "isDefault") {
        studentAddress += userAddress[key] + ", ";
      }
    }
    studentAddress = studentAddress.slice(0, -2);
  }

  const handleAddStudent = () => {
    setIsAddStudentModalVisible(true);
  };

  const handleStudentAdded = () => {
    fetchAllActiveStudents();
  };

  return (
    <>
      <div className="student">
        <div className="student_header">
          <h2>Enrolled Students</h2>
        </div>
        {isLoading && <Loader />}
        <div className="student_content">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
            <Input
              placeholder="Search by Name or Email"
              prefix={<SearchOutlined />}
              value={search}
              onChange={handleSearch}
              style={{ width: "220px" }}
            />
            <Button
              icon={isAscending === true ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
              onClick={() => setIsAscending((prev) => (prev === true ? false : true))}
              title={isAscending === true ? "Sort Z → A" : "Sort A → Z"}
            />
          </div>
        </div>

        <Table
          columns={studentColumns}
          dataSource={
            isAscending === null
              ? tableData
              : [...tableData].sort((a, b) => {
                  const nameA = (a.studentName || "").toLowerCase();
                  const nameB = (b.studentName || "").toLowerCase();
                  return isAscending
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
                })
          }
          loading={isLoading}
          pagination={false}
          rowKey="key"
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            current={pageIndex}
            total={totalRecords}
            pageSize={pageSize}
            showSizeChanger
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
            style={{ marginTop: 16 }}
          />
        </div>

        <Modal
          title="Personal Details"
          open={isModalVisible}
          onCancel={handleCancelMedicalDetailsModal}
          footer={null}
        >
          {medicalDetails ? (
            <div className="medical-details-card">
              <p>
                <strong>Height:</strong> {medicalDetails.medicalFitness.height}{" "}
                {medicalDetails.height && "cm"}
              </p>
              <p>
                <strong>Weight:</strong> {medicalDetails.medicalFitness.weight}{" "}
                {medicalDetails.weight && "kg"}
              </p>
              <p>
                <strong>Blood Pressure:</strong>{" "}
                {medicalDetails.medicalFitness.bloodPressure}
              </p>
              <p>
                <strong>Address: </strong>
                {studentAddress}
              </p>
            </div>
          ) : (
            <p>No medical details available.</p>
          )}
        </Modal>
      </div>
      <Modal
        isVisible={isAddStudentModalVisible}
        onClose={() => setIsAddStudentModalVisible(false)}
        onStudentAdded={handleStudentAdded}
      />
    </>
  );
};

export default ActiveStudents;